import React from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Link as RouterLink } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import BookIcon from '@mui/icons-material/Book';
import HomeIcon from '@mui/icons-material/Home';

// Styled components for modern design
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: '#1a1a2e',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
  borderBottom: '1px solid rgba(102, 126, 234, 0.1)',
  color: '#e2e8f0',
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  padding: theme.spacing(0, 3),
  minHeight: '80px',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0, 2),
    minHeight: '70px',
  },
}));

const Logo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  textDecoration: 'none',
  color: 'inherit',
  '&:hover': {
    textDecoration: 'none',
    color: '#667eea',
  },
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: '1.5rem',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.25rem',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1, 2),
  fontWeight: 600,
  textTransform: 'none',
  color: '#e2e8f0',
  '&:hover': {
    background: 'rgba(102, 126, 234, 0.1)',
    color: '#667eea',
  },
}));

const MainContent = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(4),
  minHeight: 'calc(100vh - 160px)',
}));

const BackgroundPattern = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: '#0f0f23',
  zIndex: -1,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      radial-gradient(circle at 20% 50%, rgba(102, 126, 234, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(118, 75, 162, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 40% 20%, rgba(102, 126, 234, 0.03) 0%, transparent 50%)
    `,
  },
});

// Simple top-bar layout wrapper that provides a Material-UI AppBar and centers
// page content inside a responsive Container. Children are rendered below the
// toolbar to avoid being hidden under the fixed AppBar.

export default function Layout({ children }) {
  return (
    <>
      <BackgroundPattern />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <StyledAppBar position="fixed" elevation={0}>
          <StyledToolbar>
            <Logo
              component={RouterLink}
              to="/"
              sx={{ flexGrow: 1 }}
            >
              <BookIcon sx={{ fontSize: '2rem', color: '#667eea' }} />
              <LogoText variant="h6" component="div">
                Concurso Study
              </LogoText>
            </Logo>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <StyledButton
                component={RouterLink}
                to="/"
                startIcon={<HomeIcon />}
                size="medium"
              >
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  Início
                </Box>
              </StyledButton>
            </Box>
          </StyledToolbar>
        </StyledAppBar>

        {/* Spacer to push content below the fixed app bar */}
        <Box sx={{ height: { xs: '70px', sm: '80px' } }} />

        <MainContent maxWidth="lg" sx={{ flexGrow: 1, py: 4 }}>
          <Box className="fade-in">
            {children}
          </Box>
        </MainContent>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            background: '#1a1a2e',
            borderTop: '1px solid rgba(102, 126, 234, 0.1)',
            py: 3,
            px: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="body1" sx={{ color: '#cbd5e1' }}>
            May❤️ the force be with you
          </Typography>
        </Box>
      </Box>
    </>
  );
}
